import { useEffect, useRef } from "react"
import {
	useSecuritySectionStore,
	SECURITY_FLOWS,
	MfaMethod
} from "./stores/securitySectionStore"
import SecurityOverview from "./sections/SecurityOverview"
import { EmailChange } from "./sections/EmailChange"
import { PasswordChange } from "./sections/PasswordChange"
import { UsernameToEmail } from "./sections/UsernameToEmail"
import { MFASetupFlow } from "./sections/MFASetupFlow"
import { useAuthContext } from "@context/AuthProvider"

interface ComplianceSecuritySectionProps {
	isFullyCompliant?: boolean
}

const ComplianceSecuritySection = ({
	isFullyCompliant = false
}: ComplianceSecuritySectionProps) => {
	const { userProfile, refreshUserProfile } = useAuthContext()
	const { currentFlow, setUser, goToIdle } = useSecuritySectionStore()
	const previousFlowRef = useRef<string | null>(null)

	useEffect(() => {
		goToIdle()
	}, [goToIdle])

	useEffect(() => {
		const wasInFlow =
			previousFlowRef.current != null &&
			previousFlowRef.current !== SECURITY_FLOWS.IDLE
		const isNowIdle = currentFlow === SECURITY_FLOWS.IDLE
		if (wasInFlow && isNowIdle) {
			refreshUserProfile()
		}
		previousFlowRef.current = currentFlow
	}, [currentFlow, refreshUserProfile])

	useEffect(() => {
		if (!userProfile) return
		const currentUser = useSecuritySectionStore.getState().user
		const profilePrimary = userProfile.primaryMFA?.trim().toUpperCase() as
			| MfaMethod
			| undefined
		const profileSecondary = userProfile.secondaryMFA?.trim().toUpperCase() as
			| MfaMethod
			| undefined
		setUser({
			email: userProfile.agentEmailAddress || "",
			emailVerified:
				currentUser?.emailVerified || (userProfile.emailValidatedFlag ?? false),
			username: userProfile.loginUserName || "",
			phone: userProfile.telephoneNumber || "",
			phoneVerified:
				currentUser?.phoneVerified ??
				userProfile.mobilePhoneValidatedFlag ??
				false,
			primaryMFA:
				(profilePrimary && ["EMAIL", "SMS"].includes(profilePrimary)
					? profilePrimary
					: null) ??
				currentUser?.primaryMFA ??
				null,
			secondaryMFA:
				(profileSecondary && ["EMAIL", "SMS"].includes(profileSecondary)
					? profileSecondary
					: null) ??
				currentUser?.secondaryMFA ??
				null,
			emailIsUsername:
				currentUser?.emailIsUsername || (userProfile.emailIsUsername ?? false),
			lastPasswordUpdate: userProfile.lastPasswordChangeDate || ""
		})
	}, [userProfile])

	const renderCurrentFlow = () => {
		switch (currentFlow) {
			case SECURITY_FLOWS.USERNAME_TO_EMAIL:
				return <UsernameToEmail />
			case SECURITY_FLOWS.EMAIL_CHANGE:
				return <EmailChange />
			case SECURITY_FLOWS.PASSWORD_CHANGE:
				return <PasswordChange />
			case SECURITY_FLOWS.MFA_SETUP:
				return <MFASetupFlow />
			default:
				return <SecurityOverview isFullyCompliant={isFullyCompliant} />
		}
	}

	return renderCurrentFlow()
}

export default ComplianceSecuritySection
